import { PARTY_PASS_CHECKOUT_SESSION_PLACEHOLDER } from "./party-pass-celebration";
import { isSafeAppRelativePath } from "./party-splits";

export const NATIVE_PARTY_PASS_CHECKOUT_UNAVAILABLE =
  "nativePartyPassCheckoutUnavailable";

/**
 * Must match `scheme` in apps/mobile/app.config.ts. Stripe Checkout returns
 * through this scheme so the in-app browser can close.
 */
export const NATIVE_APP_URL_SCHEME = "awardparty";
export const NATIVE_CHECKOUT_CALLBACK_URL = `${NATIVE_APP_URL_SCHEME}://`;
export const NATIVE_CHECKOUT_RETURN_PATH = "/checkout/return";
export const NATIVE_CHECKOUT_RETURN_PARAM = "next";
export const NATIVE_EXTERNAL_CHECKOUT_DISMISSED_EVENT =
  "award-party:external-checkout-dismissed";

export type NativeAppPlatform = "ios" | "android";

export interface NativePartyPassCheckoutContext {
  platform: NativeAppPlatform;
  /**
   * The native shell obtains this from the active storefront, not device locale:
   * StoreKit uses ISO 3166-1 alpha-3 and Play Billing uses alpha-2.
   */
  storefrontCountryCode?: string;
  /**
   * Provided by the Android shell only after its Play alternative-billing
   * integration and transaction reporting are active.
   */
  androidAlternativeBillingEnabled?: boolean;
  /**
   * Set by shells that close Stripe's browser on the app-scheme return.
   * Older shells omit it and keep the hosted success page.
   */
  checkoutReturn?: boolean;
}

export function parseNativePartyPassCheckoutContext(
  value: unknown,
): NativePartyPassCheckoutContext | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const nativeContext = value as Record<string, unknown>;
  const { platform, storefrontCountryCode } = nativeContext;
  const validCountryCode =
    typeof storefrontCountryCode === "string" &&
    (platform === "ios"
      ? /^[A-Z]{3}$/.test(storefrontCountryCode)
      : platform === "android" && /^[A-Z]{2}$/.test(storefrontCountryCode));
  if (
    (platform !== "ios" && platform !== "android") ||
    !validCountryCode
  ) {
    return undefined;
  }

  const androidAlternativeBillingEnabled =
    typeof nativeContext.androidAlternativeBillingEnabled === "boolean"
      ? nativeContext.androidAlternativeBillingEnabled
      : undefined;
  const checkoutReturn =
    nativeContext.checkoutReturn === true ? true : undefined;

  return {
    platform,
    storefrontCountryCode,
    ...(androidAlternativeBillingEnabled === undefined
      ? {}
      : { androidAlternativeBillingEnabled }),
    ...(checkoutReturn === undefined ? {} : { checkoutReturn }),
  };
}

export type PartyPassNativeCheckoutResolution =
  | { available: true; openExternally: false }
  | { available: true; openExternally: true }
  | {
      available: false;
      errorCode: typeof NATIVE_PARTY_PASS_CHECKOUT_UNAVAILABLE;
    };

/**
 * Maps the store policy supplied by a native shell to the payment behavior.
 * A missing context is a regular hosted web browser, which continues using
 * the existing Stripe Checkout redirect.
 */
export function resolvePartyPassNativeCheckout(
  context?: NativePartyPassCheckoutContext,
  options: { androidAlternativeBillingEnabled?: boolean } = {},
): PartyPassNativeCheckoutResolution {
  if (!context) {
    return { available: true, openExternally: false };
  }

  if (
    context.platform === "ios" &&
    context.storefrontCountryCode === "USA"
  ) {
    return { available: true, openExternally: true };
  }

  if (
    context.platform === "android" &&
    context.storefrontCountryCode === "US" &&
    (options.androidAlternativeBillingEnabled ??
      context.androidAlternativeBillingEnabled)
  ) {
    return { available: true, openExternally: true };
  }

  return {
    available: false,
    errorCode: NATIVE_PARTY_PASS_CHECKOUT_UNAVAILABLE,
  };
}

function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function isNativePartyReturnPath(path: string | null | undefined): path is string {
  if (
    !path ||
    !isSafeAppRelativePath(path) ||
    path.includes("..") ||
    /[\u0000-\u001F\u007F]/.test(path)
  ) {
    return false;
  }
  const pathname = path.split("?")[0]?.split("#")[0] ?? "";
  return pathname === "/parties" || pathname.startsWith("/parties/");
}

/**
 * Public HTTPS URL Stripe can redirect to. The `{CHECKOUT_SESSION_ID}`
 * placeholder stays literal so Stripe can substitute the real session id.
 * The page answers with the app scheme, which closes the in-app browser.
 */
export function buildNativeCheckoutReturnUrl(
  baseUrl: string,
  appPath: string,
): string | null {
  let origin: string;
  try {
    origin = new URL(baseUrl).origin;
  } catch {
    return null;
  }

  const probe = appPath.split(PARTY_PASS_CHECKOUT_SESSION_PLACEHOLDER).join("cs_test");
  if (!isNativePartyReturnPath(probe)) {
    return null;
  }

  const encoded = appPath
    .split(PARTY_PASS_CHECKOUT_SESSION_PLACEHOLDER)
    .map((part) => encodeURIComponent(part))
    .join(PARTY_PASS_CHECKOUT_SESSION_PLACEHOLDER);

  return `${origin}${NATIVE_CHECKOUT_RETURN_PATH}?${NATIVE_CHECKOUT_RETURN_PARAM}=${encoded}`;
}

/** App-scheme URL that dismisses the Stripe browser and reopens the shell. */
export function nativeAppUrlForCheckoutReturn(requestUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(requestUrl);
  } catch {
    return null;
  }

  if (normalizePathname(url.pathname) !== NATIVE_CHECKOUT_RETURN_PATH) {
    return null;
  }

  const next = url.searchParams.get(NATIVE_CHECKOUT_RETURN_PARAM);
  if (!isNativePartyReturnPath(next)) {
    return null;
  }

  return `${NATIVE_CHECKOUT_CALLBACK_URL}${next}`;
}

/**
 * Hosted party URL to load inside the signed-in WebView after Checkout closes.
 * An app-scheme callback keeps its path and uses the shell's configured origin.
 */
export function partyUrlFromCheckoutReturn(
  returnUrl: string,
  webAppUrl: string,
): string | null {
  let incoming: URL;
  let app: URL;
  try {
    incoming = new URL(returnUrl);
    app = new URL(webAppUrl);
  } catch {
    return null;
  }

  let path: string | null = null;
  if (incoming.protocol === `${NATIVE_APP_URL_SCHEME}:`) {
    // awardparty:///parties/id keeps an empty host. Some OS parsers treat
    // the first segment as the host: awardparty://parties/id.
    path = incoming.hostname
      ? incoming.hostname.includes(".")
        ? null
        : `/${incoming.hostname}${incoming.pathname}${incoming.search}${incoming.hash}`
      : `${incoming.pathname}${incoming.search}${incoming.hash}`;
  } else if (
    incoming.origin === app.origin &&
    normalizePathname(incoming.pathname) === NATIVE_CHECKOUT_RETURN_PATH
  ) {
    path = incoming.searchParams.get(NATIVE_CHECKOUT_RETURN_PARAM);
  }

  if (!isNativePartyReturnPath(path)) {
    return null;
  }

  const target = new URL(path, app.origin);
  if (target.origin !== app.origin) {
    return null;
  }
  return target.toString();
}
